import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { feedbackApi, adminApi, eventsApi, clientSelfImprovementApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { FeedbackItem, AITraceItem, Event, AppUser } from '../../types';

const PAGE_LIMIT = 50;

const STATUS_COLORS: Record<string, string> = {
  new:         'bg-blue-50 text-blue-700 border-blue-200',
  triaged:     'bg-yellow-50 text-yellow-700 border-yellow-200',
  in_progress: 'bg-orange-50 text-orange-700 border-orange-200',
  in_review:   'bg-purple-50 text-purple-700 border-purple-200',
  resolved:    'bg-green-50 text-green-700 border-green-200',
  wont_fix:    'bg-gray-100 text-gray-600 border-gray-300',
  closed:      'bg-gray-100 text-gray-500 border-gray-200',
};

const CATEGORY_LABELS: Record<string, string> = {
  accuracy: 'Accuracy', hallucination: 'Hallucination', tone: 'Tone',
  latency: 'Latency', ui: 'UI', other: 'Other',
};

const REASON_STYLE: Record<string, string> = {
  knowledge_gap: 'bg-amber-50 text-amber-700 border-amber-200',
  guardrail:     'bg-red-50 text-red-700 border-red-200',
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function truncate(s: string | undefined | null, n: number) {
  if (!s) return '—';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-sm py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-gray-500 w-36 shrink-0">{label}</span>
      <span className="text-gray-900 font-medium break-all">{value ?? '—'}</span>
    </div>
  );
}

function Badge({ value, styleMap }: { value: string | null | undefined; styleMap: Record<string, string> }) {
  if (value == null) return <span className="text-gray-400">—</span>;
  const cls = styleMap[value] ?? 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {value}
    </span>
  );
}

export default function NegativeFeedback() {
  const { isAdmin, user } = useAuth();
  const listTraces = isAdmin ? adminApi.listAITraces.bind(adminApi) : clientSelfImprovementApi.listTraces.bind(clientSelfImprovementApi);

  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Client / KA filters
  const [clients, setClients] = useState<AppUser[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  // Modal
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [trace, setTrace] = useState<AITraceItem | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingClients(true);
    try {
      const resp = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      setClients((resp.data as any)?.data?.users || []);
    } catch { /* ignore */ } finally {
      setLoadingClients(false);
    }
  }, [isAdmin]);

  const fetchEvents = useCallback(async (clientId?: string) => {
    try {
      const params: any = { limit: 100 };
      if (isAdmin && clientId) params.clientId = clientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const resp = await eventsApi.getAll(params);
      setEvents(resp.data.data || []);
    } catch { /* ignore */ }
  }, [isAdmin, user?._id]);

  useEffect(() => {
    if (isAdmin) fetchClients();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    setSelectedEventId('');
    fetchEvents(selectedClientId || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  const fetchItems = useCallback(async (p: number, eventId: string, clientId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await feedbackApi.list({
        sentiment: 'negative',
        page: p,
        limit: PAGE_LIMIT,
        ...(eventId ? { eventId } : {}),
        ...(isAdmin && clientId ? { clientId } : {}),
      });
      const raw = res.data as any;
      const data = raw?.data ?? raw;
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
        setTotalPages(1);
      } else {
        setItems(data?.items ?? data?.feedback ?? []);
        setTotal(data?.total ?? 0);
        setTotalPages(data?.pages ?? 1);
      }
    } catch {
      setError('Failed to load negative feedback.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    setPage(1);
    fetchItems(1, selectedEventId, selectedClientId);
  }, [selectedEventId, selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchItems(page, selectedEventId, selectedClientId);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openModal(item: FeedbackItem) {
    setSelected(item);
    setTrace(null);
    if (!item.messageId) return;

    setTraceLoading(true);
    try {
      const res = await listTraces({ messageId: item.messageId, limit: 10 });
      const traces: AITraceItem[] = res.data?.data?.traces ?? [];
      // Prefer the parent chat_request_stream / chat_request trace which has quality fields
      const parent = traces.find(t =>
        t.operation === 'chat_request_stream' || t.operation === 'chat_request'
      ) ?? traces[0] ?? null;
      setTrace(parent);
    } catch {
      // trace stays null — graceful degradation
    } finally {
      setTraceLoading(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Negative Feedback</h1>
          <p className="text-sm text-gray-500 mt-1">
            Messages where users gave a thumbs-down — showing the question, bot response, and AI trace analysis.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <select
              value={selectedClientId}
              onChange={e => setSelectedClientId(e.target.value)}
              disabled={loadingClients}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>{c.name || c.email}</option>
              ))}
            </select>
          )}
          <select
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
          >
            <option value="">All Knowledge Assistants</option>
            {events.map(e => (
              <option key={e._id} value={e._id}>{e.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Loading…</div>
          ) : error ? (
            <div className="flex items-center justify-center py-16 text-red-500 text-sm">{error}</div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">No negative feedback found.</div>
          ) : (
            <>
              <div className="text-xs text-gray-400 mb-3">{total} total</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left py-2 pr-4 font-medium w-32">Date</th>
                      <th className="text-left py-2 pr-4 font-medium">Question Asked</th>
                      <th className="text-left py-2 pr-4 font-medium">Bot Response</th>
                      <th className="text-left py-2 pr-4 font-medium w-28">Status</th>
                      <th className="text-left py-2 font-medium w-32">User</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {items.map(item => (
                      <tr key={item.feedbackId} className="hover:bg-gray-50 cursor-pointer" onClick={() => openModal(item)}>
                        <td className="py-3 pr-4 text-gray-500 text-xs whitespace-nowrap">{formatDate(item.createdAt)}</td>
                        <td className="py-3 pr-4 text-gray-800 max-w-[220px]">
                          <span title={(item as any).userMessage ?? ''}>{truncate((item as any).userMessage, 70)}</span>
                        </td>
                        <td className="py-3 pr-4 text-gray-600 max-w-[260px]">
                          <span title={(item as any).botResponse ?? ''}>{truncate((item as any).botResponse, 80)}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS[item.status] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                            {item.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-3 text-gray-500 text-xs">{item.userName ?? item.userId ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
                  <div className="flex gap-2">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <ChevronLeft size={16} />
                    </button>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Feedback Detail</h3>
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(selected.createdAt)}</p>
              </div>
              <button onClick={() => { setSelected(null); setTrace(null); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">

              {/* Conversation */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Conversation</h4>
                {(selected as any).userMessage && (
                  <div className="flex justify-end">
                    <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3 max-w-[85%]">
                      <p className="text-xs text-indigo-400 font-medium mb-1">User</p>
                      <p className="text-sm text-gray-800">{(selected as any).userMessage}</p>
                    </div>
                  </div>
                )}
                {(selected as any).botResponse && (
                  <div className="flex justify-start">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 max-w-[85%]">
                      <p className="text-xs text-gray-400 font-medium mb-1">Bot response <span className="text-red-400 ml-1">👎</span></p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{(selected as any).botResponse}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* AI Trace analysis */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  AI Trace Analysis
                  {traceLoading && <span className="ml-2 text-gray-400 font-normal normal-case">loading…</span>}
                  {!traceLoading && !trace && <span className="ml-2 text-gray-400 font-normal normal-case">no trace found</span>}
                </h4>
                {trace && (
                  <>
                    <MetaRow label="Answered" value={
                      trace.answered === true ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-green-50 text-green-700 border-green-200">Yes</span>
                      ) : trace.answered === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-red-50 text-red-700 border-red-200">No</span>
                      ) : <span className="text-gray-400">—</span>
                    } />
                    <MetaRow label="Reason" value={
                      <Badge value={trace.refusalReason} styleMap={REASON_STYLE} />
                    } />
                    <MetaRow label="Detail" value={trace.unansweredDetail} />
                    <MetaRow label="Model" value={trace.model ?? trace.provider} />
                    {(trace.timingMs as any)?.total && (
                      <MetaRow label="Response time" value={`${(trace.timingMs as any).total} ms`} />
                    )}
                    {(trace.usage as any)?.usageMetadata && (
                      <MetaRow label="Tokens" value={
                        `${(trace.usage as any).usageMetadata.totalTokenCount ?? '?'} total`
                      } />
                    )}
                  </>
                )}
              </div>

              {/* User's feedback */}
              {(selected.details || selected.summary) && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">User's Feedback</h4>
                  {selected.summary && <p className="text-sm text-gray-700 font-medium mb-1">{selected.summary}</p>}
                  {selected.details && <p className="text-sm text-gray-700">{selected.details}</p>}
                  {selected.detailsEnglish && selected.detailsEnglish !== selected.details && (
                    <p className="text-xs text-gray-500 mt-1 italic">({selected.detailsEnglish})</p>
                  )}
                </div>
              )}

              {/* Feedback meta */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Feedback Info</h4>
                <MetaRow label="User" value={selected.userName ?? selected.userId} />
                <MetaRow label="Category" value={selected.category ? CATEGORY_LABELS[selected.category] ?? selected.category : null} />
                <MetaRow label="Status" value={
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS[selected.status] ?? ''}`}>
                    {selected.status.replace(/_/g, ' ')}
                  </span>
                } />
                {selected.priority && <MetaRow label="Priority" value={selected.priority.toUpperCase()} />}
              </div>

              {selected.resolutionNote && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Resolution Note</h4>
                  <p className="text-sm text-green-800">{selected.resolutionNote}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
